/**
 * Launch an element (no-code) test from the editor's Launch tab. Same payload,
 * plan-limit handling, tracking and cleanup as the wizard's Review step.
 */
import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useAuth } from "@/lib/contexts/AuthContext"
import { useSubscription } from "@/lib/contexts/SubscriptionContext"
import { useCreateExperiment } from "@/hooks/useExperiments"
import { usePlanUsage, useInvalidatePlanUsage } from "@/hooks/usePlanUsage"
import { getUserFacingApiError, isPlanLimitApiError, trackSplitTestSetup } from "@/lib/api/api"
import { track } from "@/lib/utils/track"
import { logExperimentEvent, reportExperimentFailure } from "@/lib/observability/experiments"
import { normalizeAudienceRestriction, normalizeGeoRestriction } from "@/lib/types/experiment-restrictions"
import type { ComponentInstruction } from "@/components/experiments/ComponentEditor/utils/instructionTypes"
import { clearSplitTestDraft, type DraftSplitTestState } from "./splitTestDraft"

export function useLaunchElementTest({ propertyId, basePath }: { propertyId: string; basePath: string }) {
  const navigate = useNavigate()
  const { token } = useAuth()
  const { openPricingModal } = useSubscription()
  const createMutation = useCreateExperiment({ propertyId })
  const { splitTests } = usePlanUsage({ propertyId })
  const invalidatePlanUsage = useInvalidatePlanUsage()

  const launch = useCallback(
    async (draft: DraftSplitTestState, variantInstructions: unknown[]) => {
      if (!splitTests.canAdd) {
        openPricingModal({ reason: "split_test_limit" })
        return false
      }
      if (variantInstructions.length === 0) {
        toast.error("Make at least one change in the editor before launching. Otherwise both versions are the same page.")
        return false
      }
      const pid = draft.selectedPropertyForTest || propertyId
      const path = draft.componentPageUrl.startsWith("/") || /^https?:/i.test(draft.componentPageUrl)
        ? draft.componentPageUrl
        : `/${draft.componentPageUrl}`
      try {
        await createMutation.mutateAsync({
          experimentName: draft.experimentName,
          variants: [
            { id: "A", url: path, label: "Control", notes: "", isControl: true },
            {
              id: "B",
              url: path,
              label: "Variant B",
              notes: "Component changes",
              isControl: false,
              instructions: variantInstructions as ComponentInstruction[],
            },
          ],
          testGoal: draft.testGoal,
          isUserLevel: draft.isUserLevel,
          geoRestriction: normalizeGeoRestriction(draft.geoRestriction),
          audienceRestriction: normalizeAudienceRestriction(draft.audienceRestriction),
          finishPageUrl: draft.finishPageUrl || undefined,
          testType: "component",
          notes: draft.experimentNotes || undefined,
          draftId: draft.draftId,
          trafficDistribution: draft.trafficDistribution,
          autoStopDurationDays: draft.autoStopDurationDays ?? undefined,
        })
        if (pid && token) trackSplitTestSetup(pid, "launch", "completed", token)
        clearSplitTestDraft(draft.draftId)
        track("experiment_published", token, {
          name: draft.experimentName,
          test_type: "element",
          goal: draft.testGoal,
          variation_count: 2,
          surface: "editor",
        })
        logExperimentEvent(
          "experiment_launched",
          { propertyId: pid },
          { experimentName: draft.experimentName, experimentType: "element", draftId: draft.draftId },
        )
        toast.success("Test launched", { description: `"${draft.experimentName}" is now running.` })
        navigate(`${basePath}/split-testing${window.location.search}`)
        return true
      } catch (e) {
        reportExperimentFailure(
          "launch",
          e,
          { propertyId: pid },
          { experimentName: draft.experimentName, experimentType: "element", draftId: draft.draftId },
        )
        if (isPlanLimitApiError(e)) {
          invalidatePlanUsage()
          toast.error("You've reached a limit of your current plan", {
            description: "This is a plan limit, not an error. Upgrade to launch this test.",
            action: { label: "Upgrade", onClick: () => openPricingModal({ reason: "split_test_limit" }) },
          })
          return false
        }
        toast.error("The test didn't launch", { description: getUserFacingApiError(e) })
        return false
      }
    },
    [splitTests.canAdd, openPricingModal, propertyId, createMutation, token, invalidatePlanUsage, navigate, basePath],
  )

  return {
    launch,
    isLaunching: createMutation.isPending,
    atLimit: !splitTests.canAdd,
    limit: splitTests.limit,
  }
}
